<template>
  <div id="login" class="login" lang="en-US">
    <div class="login__bg-blob login__bg-blob--top"></div>
    <div class="login__bg-blob login__bg-blob--bottom"></div>

    <div class="login__card">
      <div class="login__logo">
        <a href="//bookimed.com" target="_blank">
          <b>Booki</b>med
        </a>
        <p class="login__welcome">Welcome back! Sign in to continue.</p>
      </div>

      <div class="login__form">
        <div class="login__field">
          <div class="login__input-wrap" :class="{ 'has-error': errorText }">
            <input
              v-model="name"
              placeholder="Login or email"
              autocomplete="username"
              class="login__input"
            />
            <i class="tim-icons icon-email-85 login__input-icon"></i>
          </div>
        </div>

        <div class="login__field">
          <div class="login__input-wrap" :class="{ 'has-error': errorText }">
            <input
              v-model="password"
              type="password"
              placeholder="Password"
              autocomplete="current-password"
              class="login__input"
            />
            <i class="tim-icons icon-lock-circle login__input-icon"></i>
          </div>
        </div>

        <div v-if="errorText" class="login__error">{{ errorText }}</div>

        <button class="login__submit" @click="login">Sign in</button>

        <div class="login__divider">
          <span class="login__divider-line"></span>
          <span class="login__divider-text">or</span>
          <span class="login__divider-line"></span>
        </div>

        <button
          class="login__google"
          :disabled="googleLoading"
          :aria-busy="googleLoading"
          @click="loginWithGoogle"
        >
          <svg class="login__google-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>{{ googleLoading ? 'Signing in...' : 'Sign in with Google' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* global google */
import APIAuth from '@/api/modules/auth';
import axios from 'axios';
import Cookies from 'js-cookie';

export default {
  data: () => ({
    name: '',
    password: '',
    errorText: '',
    googleLoading: false,
    gsiInitialized: false,
    pendingGsi: null,
    domainType:
      process.env.NODE_ENV === 'development'
        ? '.run'
        : window.location.hostname.slice(window.location.hostname.lastIndexOf('.')),
  }),
  mounted() {
    window.addEventListener('keyup', this.handleEnter);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleEnter);
    if (this.pendingGsi) {
      clearTimeout(this.pendingGsi.watchdog);
      this.pendingGsi = null;
    }
  },
  methods: {
    handleEnter(event) {
      if (event.keyCode === 13) this.login();
    },
    checkIsAdmin(token) {
      return axios
        .get(`https://auth.bookimed${this.domainType}/api/v1/oauth/tokens/info?access_token=${token}`)
        .then((response) => {
          if (response.data && response.data.scopes && response.data.scopes.admin) {
            return response.data.scopes.admin.id === 'admin';
          }
          return false;
        })
        .catch(() => false);
    },
    setSession(Authorization) {
      return axios.post(
        `https://admin.bookimed${this.domainType}/login`,
        {},
        { headers: { Authorization } }
      );
    },
    persistTokenAndRedirect(token) {
      this.$store.commit('agencies/RESET_PERMISSIONS');
      this.$store.commit('employees/RESET_PERMISSIONS');
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      Cookies.set('vT', window.btoa(token), { path: '/', domain, expires: 1 / 3 });
      Cookies.set('adminToken', token, { path: '/', domain, expires: 1 / 3 });
      if (this.$route.query.redirectUrl) {
        window.open(window.atob(this.$route.query.redirectUrl), '_self');
      } else {
        this.$router.push({ name: 'Doctors' });
      }
    },
    async login() {
      if (!this.name || !this.password) {
        this.errorText = 'Login or Email can not be empty';
        return;
      }
      this.errorText = '';
      const bodyFormData = new FormData();
      bodyFormData.set('login', this.name);
      bodyFormData.set('password', this.password);
      this.$store.dispatch('setLoader', true);

      try {
        const response = await APIAuth.userAuthorization(bodyFormData);
        const token = response && response.data && response.data.token;
        if (!token) {
          this.errorText = 'Incorrect credentials. Try again.';
          return;
        }
        const isAdmin = await this.checkIsAdmin(token);
        if (!isAdmin) {
          this.errorText = 'Incorrect credentials. Try again.';
          return;
        }
        this.persistTokenAndRedirect(token);
        await this.setSession(token);
      } catch (e) {
        this.errorText = 'Incorrect credentials. Try again.';
      } finally {
        this.$store.dispatch('setLoader', false);
      }
    },
    handleGsiCredential({ credential }) {
      const p = this.pendingGsi;
      if (!p) return;
      this.pendingGsi = null;
      clearTimeout(p.watchdog);
      p.handle(credential);
    },
    async loginWithGoogle() {
      if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) {
        this.errorText = 'Google Sign-In failed to load. Please reload the page.';
        return;
      }

      this.googleLoading = true;
      this.errorText = '';

      if (this.gsiInitialized) google.accounts.id.cancel();
      if (this.pendingGsi) {
        clearTimeout(this.pendingGsi.watchdog);
        this.pendingGsi = null;
      }

      if (!this.gsiInitialized) {
        google.accounts.id.initialize({
          client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
          callback: this.handleGsiCredential,
        });
        this.gsiInitialized = true;
      }

      try {
        await new Promise((resolve, reject) => {
          // FedCM makes notification-moment methods unreliable; use a timeout watchdog.
          const watchdog = setTimeout(() => {
            this.pendingGsi = null;
            reject(new Error('Google Sign-In timeout'));
          }, 60000);

          this.pendingGsi = {
            watchdog,
            handle: async (credential) => {
              try {
                const { data } = await APIAuth.googleLogin(credential);
                const token = data && data.token;
                if (!token) {
                  reject(new Error('Missing token in response'));
                  return;
                }
                const isAdmin = await this.checkIsAdmin(token);
                if (!isAdmin) {
                  reject({ response: { status: 403 } });
                  return;
                }
                this.persistTokenAndRedirect(token);
                await this.setSession(token);
                resolve();
              } catch (err) {
                reject(err);
              }
            },
          };

          google.accounts.id.prompt();
        });
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status === 403) {
          this.errorText = 'Access denied. Use your @bookimed.com account.';
        } else if (status === 404) {
          this.errorText = 'Account not found. Contact your administrator.';
        } else if (err && err.message === 'Google Sign-In timeout') {
          this.errorText = 'Google Sign-In timed out. Please try again.';
        } else {
          this.errorText = 'Google sign-in failed. Please try again.';
        }
      } finally {
        this.googleLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$primary: #a3cc0e;
$primary-dark: #8bb30b;

.login {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
  background: linear-gradient(135deg, #ecfdf5 0%, #ffffff 50%, #e0f2fe 100%);
}

.login__bg-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;

  &--top {
    top: -80px;
    left: -80px;
    width: 320px;
    height: 320px;
    background: rgba(163, 204, 14, 0.25);
  }
  &--bottom {
    bottom: -96px;
    right: -64px;
    width: 384px;
    height: 384px;
    background: rgba(56, 189, 248, 0.3);
  }
}

.login__card {
  position: relative;
  width: 100%;
  max-width: 420px;
  padding: 48px 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(6px);
}

.login__logo {
  text-align: center;
  margin-bottom: 40px;

  a {
    font-family: 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1f2937;
    text-decoration: none;
    letter-spacing: -0.02em;

    b {
      font-weight: 700;
    }
  }
}
.login__welcome {
  margin: 12px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.login__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login__field {
  margin: 0;
}

.login__input-wrap {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: $primary;
    box-shadow: 0 0 0 4px rgba(163, 204, 14, 0.2);
  }

  &.has-error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
  }
}
.login__input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  outline: none;
  font-size: 14px;
  color: #1f2937;
  padding: 4px 0;

  &::placeholder {
    color: #9ca3af;
  }
}
.login__input-icon {
  font-size: 16px;
  color: #9ca3af;
  margin-left: 8px;
}

.login__error {
  color: #ef4444;
  font-size: 12px;
  margin-top: -8px;
}

.login__submit {
  width: 100%;
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  background: $primary;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 20px -5px rgba(163, 204, 14, 0.35);
  transition: filter 0.15s, box-shadow 0.15s;

  &:hover {
    filter: brightness(0.95);
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px rgba(163, 204, 14, 0.3);
  }
}

.login__divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0;
}
.login__divider-line {
  flex: 1;
  border-top: 1px solid #e5e7eb;
}
.login__divider-text {
  font-size: 12px;
  color: #9ca3af;
}

.login__google {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: background 0.15s, box-shadow 0.15s;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.05);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}
.login__google-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
</style>
